/**
 * `GET /v1/products/{id}/linkset` — the merge preview, and the shape the scan simulator
 * renders.
 *
 * `acceptance-tests.md` is explicit that the scan preview is **the real merge, never a
 * client-side guess**, and this is the endpoint that makes that true: it composes
 * `buildEntry` over the caller's current rows with `mergeLinkset` and `negotiate` — the
 * same three functions the resolver runs, all of them in `packages/gs1` or `packages/db`
 * after R8. The mechanical half of the same rule is a dependency-cruiser rule forbidding
 * `apps/app -> @packagepage/gs1`.
 *
 * **What is still spelled twice is the composition**, and that is deliberate rather than
 * conceded: negotiate-then-entry-order is assembled here and in the resolver's `chooseOne`,
 * because the API cannot import an app. c32's worst finding of the phase was an *ordering*
 * between two correct functions, so the parity probe drives both sides **over HTTP** — a
 * pure-function comparison would move both through the shared code and prove nothing.
 *
 * ## The parameter names are this file's invention, and it says so
 *
 * `Technical Requirements` §5 has listed `/products/{id}/linkset  GET — server-side merge
 * preview for given qualifiers (debug/UX)` since its first draft and **names no
 * parameters**. The Block D re-plan §1.4 writes them as `?cpv=&lot=&serial=&lang=&linkType=`,
 * and the plan is not a source (c30's rule). So these are declared here as additions §5 is
 * owed, recorded in the Drift Log and written into §5 by this commit — exactly the way
 * `products.ts` handles `sort` and `order`, which §5 owes for the same reason.
 *
 * `accept` is a sixth, beyond even §1.4's five. `negotiate` takes two headers and a preview
 * that can express only one is honest about language and silent about media — and media is
 * a dimension the frozen contract table already pins three rows of. Omitting it means "the
 * client stated no preference", which is what a scan with no `Accept` sends.
 *
 * ## `resolving` is `published_at`, never `status`
 *
 * c34 shipped an advisory whose predicate was `status !== "published"` and it fired on a
 * product that was resolving perfectly: `archived` is reachable from `published`, archiving
 * is resolution-neutral by ruling (R2), and the entry goes on being served unchanged. **The
 * rule this endpoint inherits: `status` is what the brand's catalogue says; the entry is
 * what a scanner gets, and archiving deliberately decouples them.** Anything asking *"is
 * this code answering?"* asks `published_at`. Both are on the response, side by side, so
 * the screen never has to pick the wrong one.
 */
import { z } from "zod";
import { PRODUCT_STATUSES } from "../catalog.js";
import { CHARSET_X, CURIE } from "./patterns.js";
/**
 * A qualifier as it arrives on the query string.
 *
 * Held to charset X for the same reason the link writer holds it: a value no scanner can
 * encode describes a scan that cannot happen, and a preview of an impossible scan is worse
 * than a refusal. Absent and `""` both mean "not present in the scanned code" — `""` is
 * what an empty `<select>` sends, and refusing it would make the simulator's own controls
 * produce a 400.
 */
const previewQualifier = (key) => z.preprocess((value) => (value === undefined || value === null || value === "" ? null : value), z
    .string(`${key} must be 1–20 characters of GS1 charset X.`)
    .regex(CHARSET_X, `${key} must be 1–20 characters of GS1 charset X.`)
    .nullable());
const optionalHeaderValue = z.preprocess((value) => (typeof value === "string" && value !== "" ? value : null), z.string().nullable());
export const linksetPreviewQuerySchema = z
    .object({
    cpv: previewQualifier("cpv"),
    lot: previewQualifier("lot"),
    serial: previewQualifier("serial"),
    /**
     * The link type a machine client would name, or absent for a plain camera scan.
     *
     * **Absent is the interesting case and the one the simulator defaults to.** A phone
     * sends no `?linkType=`, so it follows the default slot — which is the whole subject
     * of `Qualifiers & Inheritance`'s table and of the default-slot ruling.
     */
    linkType: z.preprocess((value) => (value === undefined || value === null || value === "" ? null : value), z
        .string("linkType must be a CURIE such as gs1:pip.")
        .regex(CURIE, "linkType must be a CURIE such as gs1:pip.")
        .nullable()),
    /** An `Accept-Language` header, verbatim — `en-GB,en;q=0.9` is what a real phone sends. */
    lang: optionalHeaderValue,
    /** An `Accept` header, verbatim. See the file header on why this exists. */
    accept: optionalHeaderValue,
})
    .describe("GET /v1/products/{id}/linkset — the merge preview's qualifiers.");
/**
 * Why a scan of this code would not reach anything, or null.
 *
 * Two values, and each is a **defined answer rather than a 500** — §1.4's requirement, and
 * the reason this is an enum on a 200 rather than a problem body. A preview that refused
 * would be least useful exactly when a brand most needs to see what is missing.
 *
 *   - `not_published` — nothing has ever been published for this code, so a scan gets a
 *     404 from the resolver honestly: there is no mirrored entry to answer from.
 *   - `no_default_slot` — the rows cannot make a legal entry, because no link holds the
 *     GTIN grain's default slot. `buildEntry` refuses it, publishing would refuse it, and
 *     a plain camera scan would have nowhere to go.
 */
export const LINKSET_PREVIEW_BLOCKERS = ["not_published", "no_default_slot"];
/**
 * What the resolver would decide, in its own vocabulary.
 *
 * A subset of `SCAN_OUTCOMES`, not a new list: these are the outcomes reachable from a
 * preview, which has no `linkset` request and — like the camera path — no way to reach a
 * `multiple_choices`. `unknown_gtin` is absent because the product was found by id; a code
 * this organization does not carry is a 404 from the route, not an outcome.
 */
export const LINKSET_PREVIEW_OUTCOMES = [
    "default",
    "linktype",
    "tombstone",
    "degraded",
    "churned",
    "no_live_default",
    "no_such_link_type",
];
/**
 * One candidate the merge kept, with the three facts that explain where it sits.
 *
 * `grain` is the link's own, not the request's — the simulator shows a GTIN-grain link
 * answering a lot-coded scan and needs to say **why**, which is inheritance, which is the
 * thing `Qualifiers & Inheritance` exists to make predictable.
 */
export const previewLinkSchema = z.object({
    linkType: z.string(),
    href: z.string(),
    title: z.string().nullable(),
    hreflang: z.array(z.string()).nullable(),
    mediaType: z.string().nullable(),
    grain: z.object({
        cpv: z.string().nullable(),
        lot: z.string().nullable(),
        serial: z.string().nullable(),
    }),
    /** The stored flag: this link was designated its grain's default. */
    isDefault: z.boolean(),
    /**
     * It survived the default-shadowing pass and is a candidate for a **plain** scan.
     *
     * Not the same as `isDefault`: a GTIN-grain default is flagged and still loses the plain
     * scan to a finer grain's occupant. That difference is precisely what a user who has
     * never read a GS1 spec is being asked to predict.
     */
    holdsDefaultSlot: z.boolean(),
    /** It survived content negotiation for the language and media this request stated. */
    negotiated: z.boolean(),
    /** It is where this scan lands. Exactly one candidate carries it, or none. */
    selected: z.boolean(),
    /** Non-null means the recall flow materialised this row (§3.8). */
    recallId: z.uuid().nullable(),
});
export const linksetPreviewSchema = z.object({
    productId: z.uuid(),
    gtin14: z.string(),
    /**
     * The **catalogue** fact and the **resolution** fact, both present and neither derived
     * from the other. See the file header — Phase 2 has confused these once already.
     */
    status: z.enum(PRODUCT_STATUSES),
    resolving: z.boolean(),
    blocked: z.enum(LINKSET_PREVIEW_BLOCKERS).nullable(),
    /** The sentence a person reads. Composed by the op, never by the screen. */
    blockedDetail: z.string().nullable(),
    /** The scan this is a preview of, echoed back — the simulator labels its own answer. */
    request: z.object({
        path: z.string(),
        cpv: z.string().nullable(),
        lot: z.string().nullable(),
        serial: z.string().nullable(),
        linkType: z.string().nullable(),
        lang: z.string().nullable(),
        accept: z.string().nullable(),
    }),
    outcome: z.enum(LINKSET_PREVIEW_OUTCOMES).nullable(),
    /**
     * Where the scan lands, absolute.
     *
     * `null` only when nothing answers — a `no_such_link_type` request, or a blocked
     * product. The platform fallback is a destination like any other and is reported as one,
     * because to a consumer holding a carton it is exactly that.
     */
    destination: z.string().nullable(),
    /** The chosen candidate, or null when the destination is the platform's own page. */
    selected: previewLinkSchema.nullable(),
    /** Every candidate the merge kept for this scan, in entry order. */
    links: z.array(previewLinkSchema),
    /**
     * The recall whose scope this scan matched, or null.
     *
     * §3.8's definition, and it is a fact about the **request** rather than about where it
     * landed: a `?linkType=gs1:pip` request for a recalled lot lands on the brand's page and
     * has still matched the scope.
     */
    recallId: z.uuid().nullable(),
});
//# sourceMappingURL=linkset.js.map